<template>
  <div
    v-if="value"
    class="floating-calculator"
    :style="{ left: `${position.x}px`, top: `${position.y}px` }"
  >
    <v-card class="calculator-card pa-2">
      <v-btn class="close-btn" icon @click="cancel">
        <v-icon small color="grey"> mdi-close </v-icon>
      </v-btn>
      <v-card-text class="pa-0">
        <div class="drag-handle mb-1 py-2" @mousedown.prevent="startDrag">
          <span>{{ fieldTitle }}</span>
        </div>

        <div class="display-box mb-2">
          <div class="display-content">
            <div class="expression-line">{{ expressionValue }}</div>
            <div class="result-line">
              <span v-if="displaySymbol" class="currency-symbol">{{ displaySymbol }}</span>
              <span class="result-value">{{ formattedResultValue }}</span>
            </div>
          </div>
        </div>

        <div class="keypad">
          <v-btn class="key key-light" depressed @click="backspace">
            <v-icon size="18">mdi-backspace-outline</v-icon>
          </v-btn>
          <v-btn class="key key-op" depressed @click="setOperator('*')">×</v-btn>
          <v-btn class="key key-op" depressed @click="setOperator('/')">÷</v-btn>
          <v-btn class="key key-light" depressed @click="clearAll">C</v-btn>

          <v-btn class="key key-op" depressed @click="setOperator('-')">-</v-btn>
          <v-btn class="key key-light" depressed @click="appendDigit('9')">9</v-btn>
          <v-btn class="key key-light" depressed @click="appendDigit('8')">8</v-btn>
          <v-btn class="key key-light" depressed @click="appendDigit('7')">7</v-btn>

          <v-btn class="key key-op" depressed @click="setOperator('+')">+</v-btn>
          <v-btn class="key key-light" depressed @click="appendDigit('6')">6</v-btn>
          <v-btn class="key key-light" depressed @click="appendDigit('5')">5</v-btn>
          <v-btn class="key key-light" depressed @click="appendDigit('4')">4</v-btn>

          <v-btn class="key key-op" depressed @click="calculate">=</v-btn>
          <v-btn class="key key-light" depressed @click="appendDigit('3')">3</v-btn>
          <v-btn class="key key-light" depressed @click="appendDigit('2')">2</v-btn>
          <v-btn class="key key-light" depressed @click="appendDigit('1')">1</v-btn>

          <!-- <div class="key key-empty" /> -->
          <v-btn class="key key-op" depressed @click="appendDigit('.')">.</v-btn>
          <v-btn class="key key-light" depressed @click="appendDigit('000')">000</v-btn>
          <v-btn class="key key-light" depressed @click="appendDigit('00')">00</v-btn>
          <v-btn class="key key-light" depressed @click="appendDigit('0')">0</v-btn>
        </div>
      </v-card-text>

      <v-card-actions class="pa-0 mt-2">
        <v-btn color="primary" dark class="action-btn confirm-btn" @click="confirm">
          {{ $t("confirm") }}
        </v-btn>
        <v-btn outlined color="error" class="action-btn" @click="cancel">
          {{ $t("cancel") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "FeeCalculatorModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    initialValue: {
      type: [String, Number],
      default: 0,
    },
    anchorPoint: {
      type: Object,
      default: null,
    },
    fieldType: {
      type: String,
      default: "fee",
    },
    unitLabel: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      current: "0",
      stored: null,
      operator: null,
      resetOnNextInput: false,
      position: {
        x: 24,
        y: 80,
      },
      dragging: false,
      dragOffset: {
        x: 0,
        y: 0,
      },
    };
  },
  computed: {
    fieldTitle() {
      const titleMap = {
        fee: "fee",
        discount: "discount",
        count: "value",
        tax: "tax",
      };
      const key = titleMap[this.fieldType] || "please_import_price";
      return this.$t(key);
    },
    displaySymbol() {
      if (this.fieldType === "count" && this.unitLabel) {
        return this.unitLabel;
      }
      return this.$store.state.currency && this.$store.state.currency.symbol
        ? this.$store.state.currency.symbol
        : "";
    },
    displayValue() {
      if (!this.current || this.current === "-") return "0";
      return this.current;
    },
    formattedResultValue() {
      const raw = this.displayValue;
      if (this.fieldType === "count") {
        if (!raw || raw === "-") return "0";
        const parts = String(raw).split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return parts.join(".");
      }
      return this.$numberFormat(raw);
    },
    expressionValue() {
      if (!this.operator || this.stored == null) return "";
      const symbolMap = {
        "+": "+",
        "-": "-",
        "*": "×",
        "/": "÷",
      };
      const operatorSymbol = symbolMap[this.operator] || this.operator;
      const rightOperand = this.resetOnNextInput ? "" : this.current;
      return `${this.$numberFormat(this.stored)} ${operatorSymbol}${
        rightOperand !== "" ? ` ${this.$numberFormat(rightOperand)}` : ""
      }`;
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.$nextTick(() => {
          this.initialize();
          this.bindKeyboard();
          this.setInitialPosition();
        });
      } else {
        this.unbindKeyboard();
        this.stopDrag();
      }
    },
    initialValue() {
      if (this.value) this.initialize();
    },
    fieldType() {
      if (this.value) this.initialize();
    },
  },
  beforeDestroy() {
    this.unbindKeyboard();
    this.stopDrag();
  },
  methods: {
    initialize() {
      const raw = this.initialValue == null ? 0 : this.initialValue;
      this.current = String(raw);
      this.stored = null;
      this.operator = null;
      this.resetOnNextInput = true;
    },
    appendDigit(digit) {
      const d = String(digit);

      if (this.resetOnNextInput) {
        this.current = d === "." ? "0." : d;
        this.resetOnNextInput = false;
        return;
      }

      if (d === "." && this.current.includes(".")) return;
      if (this.current === "0" && d !== ".") {
        this.current = d;
      } else {
        this.current += d;
      }
    },
    backspace() {
      if (this.resetOnNextInput) {
        this.current = "0";
        this.resetOnNextInput = false;
        return;
      }

      if (this.current.length <= 1) {
        this.current = "0";
      } else {
        this.current = this.current.slice(0, -1);
      }
    },
    clearAll() {
      this.current = "0";
      this.stored = null;
      this.operator = null;
      this.resetOnNextInput = false;
    },
    setOperator(op) {
      if (this.operator && !this.resetOnNextInput) {
        this.calculate();
      }
      this.stored = parseFloat(this.current || 0);
      this.operator = op;
      this.resetOnNextInput = true;
    },
    calculate() {
      if (!this.operator || this.stored == null) return;

      const currentValue = parseFloat(this.current || 0);
      let result = this.stored;

      if (this.operator === "+") result = this.stored + currentValue;
      if (this.operator === "-") result = this.stored - currentValue;
      if (this.operator === "*") result = this.stored * currentValue;
      if (this.operator === "/")
        result = currentValue === 0 ? 0 : this.stored / currentValue;

      this.current = String(result);
      this.stored = null;
      this.operator = null;
      this.resetOnNextInput = true;
    },
    cancel() {
      this.$emit("cancel");
      this.$emit("input", false);
    },
    confirm() {
      if (this.operator && this.stored != null && !this.resetOnNextInput) {
        this.calculate();
      }
      const amount = parseFloat(this.current || 0);
      this.$emit("confirm", isNaN(amount) ? 0 : amount);
      this.$emit("input", false);
    },
    bindKeyboard() {
      window.addEventListener("keydown", this.handleKeyboardInput);
    },
    unbindKeyboard() {
      window.removeEventListener("keydown", this.handleKeyboardInput);
    },
    handleKeyboardInput(event) {
      if (!this.value) return;

      const key = event.key;
      const code = event.code;
      const persianDigits = "۰۱۲۳۴۵۶۷۸۹";
      const arabicDigits = "٠١٢٣٤٥٦٧٨٩";

      if (/^[0-9]$/.test(key)) {
        event.preventDefault();
        this.appendDigit(key);
        return;
      }

      const pIndex = persianDigits.indexOf(key);
      if (pIndex !== -1) {
        event.preventDefault();
        this.appendDigit(String(pIndex));
        return;
      }

      const aIndex = arabicDigits.indexOf(key);
      if (aIndex !== -1) {
        event.preventDefault();
        this.appendDigit(String(aIndex));
        return;
      }

      if (/^Numpad[0-9]$/.test(code)) {
        event.preventDefault();
        this.appendDigit(code.replace("Numpad", ""));
        return;
      }

      if (key === "." || key === ",") {
        event.preventDefault();
        this.appendDigit(".");
        return;
      }

      if (code === "NumpadDecimal") {
        event.preventDefault();
        this.appendDigit(".");
        return;
      }

      if (key === "+" || key === "-" || key === "*" || key === "/") {
        event.preventDefault();
        this.setOperator(key);
        return;
      }

      if (code === "NumpadAdd") {
        event.preventDefault();
        this.setOperator("+");
        return;
      }

      if (code === "NumpadSubtract") {
        event.preventDefault();
        this.setOperator("-");
        return;
      }

      if (code === "NumpadMultiply") {
        event.preventDefault();
        this.setOperator("*");
        return;
      }

      if (code === "NumpadDivide") {
        event.preventDefault();
        this.setOperator("/");
        return;
      }

      if (key === "×") {
        event.preventDefault();
        this.setOperator("*");
        return;
      }

      if (key === "÷") {
        event.preventDefault();
        this.setOperator("/");
        return;
      }

      if (key === "=") {
        event.preventDefault();
        this.calculate();
        return;
      }

      if (key === "Enter" || code === "NumpadEnter") {
        event.preventDefault();
        this.confirm();
        return;
      }

      if (key === "Backspace") {
        event.preventDefault();
        this.backspace();
        return;
      }

      if (key === "Delete") {
        event.preventDefault();
        this.clearAll();
        return;
      }

      if (key === "c" || key === "C" || key === "چ") {
        event.preventDefault();
        this.clearAll();
        return;
      }

      if (key === "Escape") {
        event.preventDefault();
        this.cancel();
      }
    },
    setInitialPosition() {
      const panelWidth = 326;
      const panelHeight = 362;
      const margin = 8;
      const viewportWidth = window.innerWidth || 0;
      const viewportHeight = window.innerHeight || 0;

      if (this.anchorPoint && typeof this.anchorPoint.x === "number") {
        let nextX = this.anchorPoint.x - panelWidth - 78;
        let nextY = this.anchorPoint.y - 108;

        const maxX = Math.max(margin, viewportWidth - panelWidth - margin);
        const maxY = Math.max(margin, viewportHeight - panelHeight - margin);

        nextX = Math.min(maxX, Math.max(margin, nextX));
        nextY = Math.min(maxY, Math.max(margin, nextY));

        this.position = {
          x: nextX,
          y: nextY,
        };
        return;
      }

      const centeredX = Math.max(margin, Math.floor((viewportWidth - panelWidth) / 2));
      this.position = {
        x: centeredX,
        y: 80,
      };
    },
    startDrag(event) {
      this.dragging = true;
      this.dragOffset = {
        x: event.clientX - this.position.x,
        y: event.clientY - this.position.y,
      };
      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(event) {
      if (!this.dragging) return;

      const panelWidth = 326;
      const panelHeight = 362;
      const minX = 8;
      const minY = 8;
      const maxX = Math.max(minX, window.innerWidth - panelWidth - 8);
      const maxY = Math.max(minY, window.innerHeight - panelHeight - 8);

      let nextX = event.clientX - this.dragOffset.x;
      let nextY = event.clientY - this.dragOffset.y;

      nextX = Math.min(maxX, Math.max(minX, nextX));
      nextY = Math.min(maxY, Math.max(minY, nextY));

      this.position = {
        x: nextX,
        y: nextY,
      };
    },
    stopDrag() {
      this.dragging = false;
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
    },
  },
};
</script>

<style scoped>
.floating-calculator {
  position: fixed;
  z-index: 2100;
  width: 326px;
}

.calculator-card {
  position: relative;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18) !important;
}

.close-btn {
  position: absolute;
  left: 6px;
  top: 3px;
  z-index: 2;
}

.drag-handle {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: #1f2733;
  font-size: 13px;
  font-weight: 700;
  cursor: move;
  user-select: none;
}

.display-box {
  min-height: 52px;
  border-radius: 4px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 6px 10px;
  color: #2f3742;
}

.display-content {
  width: 100%;
}

.expression-line {
  min-height: 16px;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  color: #7b8492;
}

.result-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
}

.result-value {
  flex: 1;
  text-align: left;
  direction: ltr;
  unicode-bidi: plaintext;
}

.currency-symbol {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  color: #5f6876;
  text-align: right;
}

.keypad {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
}

.key {
  min-height: 34px !important;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  color: #2f3742 !important;
  background: #fff !important;
}

.key-op {
  background: #1976d2 !important;
  color: #fff !important;
}

.key-light {
  background: #f5f5f5 !important;
}

.key-empty {
  background: transparent !important;
}

.action-btn {
  width: calc(50% - 6px);
  height: 30px !important;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
}

.confirm-btn {
  box-shadow: none !important;
}
</style>
